import AsyncStorage from "@react-native-async-storage/async-storage";
import {Alert, Dimensions, InteractionManager, Platform} from 'react-native';
import {POS_TABLET_BREAKPOINT} from './posLayout';
import i18n from '../../i18n';
import {generateObjectId} from "./objectId";
import {generateHappyId} from "./happyId";
import VivaPaymentModule from "../paymentModules/VivaPaymentsModule";
import {hasAnyConfiguredPrinter, RawBTPrintKitchen, RawBTPrintTicket} from "./rawBTDriver";
import ErrorReportingService from './ErrorReportingService';
import {isOnline} from './OfflineQueue';
import FiscatQRService from './FiscatQRService';
import {normalizeOrderType} from './orderVatResolver';
import {buildOrderServiceFeeFields, computeReceiptTotals} from './vatSummary';

// ── Offline-first: SQLite queue + SyncManager ──────────────────────────────
import {insertPendingOrder, getOrdersByStatus} from './database/OfflineSyncDatabase';
import {getCachedFoodLookup, lineItemsNeedFoodLookup} from './lineItemHydration';
import {processQueue as kickSyncQueue} from './SyncManager';
import {isFullTableSettlement} from './orderSyncReliability';
import Observables from '@happy-gastro/global-observables';

// Cross-platform toast: real ToastAndroid on Android, toast-message shim on iOS/web.
const ToastAndroid = require('./crossToast').default;

const API_URL = 'https://api.happysolutions.hu/api';

// Truly final statuses – an order in any of these can never receive more items.
const FINAL_ORDER_STATUSES = new Set(['cancelled', 'closed', 'paid', 'completed', 'done']);

/**
 * Collapse any id-shaped value (plain string, ObjectId object, `{ $oid }`,
 * populated doc) to a comparable plain id string.
 *
 * Table-order reuse hinges on matching a candidate order's `tableId` to the
 * currently selected table. A server order's `tableId` can arrive as an
 * ObjectId-shaped object while `currentTable._id` is a string (or vice-versa),
 * so a strict `===` silently misses the existing open order — the resolver then
 * mints a new id and the backend spawns a DUPLICATE order on the table (added
 * items land on the copy, the tab appears to lose the newly added items).
 * Normalizing BOTH sides before comparing removes that whole failure class.
 *
 * @param {*} value - Any id-like value.
 * @returns {string} Normalized id string, or '' when not resolvable.
 */
const normalizeId = (value) => {
    if (!value) {
        return '';
    }
    if (typeof value === 'string') {
        return value;
    }
    if (typeof value === 'object') {
        return String(value.$oid || value._id || value.id || '');
    }

    return String(value);
};

/**
 * Whether an order can still receive more items / be paid for the SAME table.
 *
 * This treats locally-queued orders that have not finished syncing
 * (`pending_sync`, or a missing status straight after the optimistic create) as
 * reusable. That is critical: when a user adds items to a dineIn order that is
 * still syncing, we MUST reuse its stable generatedId so the backend merges the
 * items, instead of spawning a duplicate order on the table. Only genuinely
 * finalized (closed/cancelled/paid) orders are excluded.
 *
 * @param {Object} candidate - Candidate order.
 * @returns {boolean}
 */
const isReusableTableOrder = (candidate) => {
    if (!candidate || candidate?.storno || candidate?.isPayed === true || candidate?.isPaid === true) {
        return false;
    }

    const normalizedStatus = String(candidate?.status || '').trim().toLowerCase();

    return !FINAL_ORDER_STATUSES.has(normalizedStatus);
};

/**
 * Read locally-queued (not-yet-synced) OPEN dineIn orders for a given table.
 *
 * These rows live only in SQLite until the SyncManager POSTs them. Without
 * surfacing them here, adding items to an order that has not finished syncing
 * would be treated as a brand-new order and create a duplicate on the table.
 * We only surface "saved for later" rows (open orders), never payment rows, and
 * reuse their stable generatedId / orderCode so no new identifiers are minted.
 *
 * @param {string} tableId - The currently selected table id.
 * @returns {Promise<Object[]>} Normalized candidate orders for the table.
 */
const getQueuedOpenOrdersForTable = async (tableId) => {
    if (!tableId) {
        return [];
    }

    try {
        const pendingRows = await getOrdersByStatus(['Pending', 'Syncing', 'Failed']);

        const normalizedTableId = normalizeId(tableId);

        return pendingRows
            .filter((row) => row?.order_data?.isSavedForLater === true)
            .map((row) => normalizePendingOrderCandidate(row))
            .filter((candidate) => candidate && normalizeId(candidate.tableId) === normalizedTableId);
    } catch (error) {
        console.warn('[paymentHelper] Failed to read queued orders for table reuse:', error?.message || error);

        return [];
    }
};

const normalizePendingOrderCandidate = (pendingRow = {}) => {
    const pendingOrderData = pendingRow?.order_data || {};
    const nestedOrder = pendingOrderData?.order && typeof pendingOrderData.order === 'object'
        ? pendingOrderData.order
        : {};
    const tableId = nestedOrder?.tableId || pendingOrderData?.currentTable?._id || pendingOrderData?.tableId;
    const tableName = nestedOrder?.tableName || pendingOrderData?.currentTable?.name || pendingOrderData?.tableName;
    const resolvedOrderId = nestedOrder?._id
        || nestedOrder?.generatedId
        || pendingOrderData?.orderId
        || pendingOrderData?._stableGeneratedId
        || pendingRow?.id;

    if (!resolvedOrderId && !tableId) {
        return null;
    }

    return {
        ...nestedOrder,
        _id: resolvedOrderId,
        generatedId: nestedOrder?.generatedId || pendingOrderData?._stableGeneratedId || pendingOrderData?.orderId || pendingRow?.id,
        orderCode: nestedOrder?.orderCode || pendingOrderData?._stableOrderCode,
        tableId,
        tableName,
        status: nestedOrder?.status || 'pending_sync',
        storno: Boolean(nestedOrder?.storno),
    };
};

const resolveExistingTableOrder = async ({ currentTable, orderId, order }) => {
    const explicitOrderId = orderId || order?._id;

    // IMPORTANT: Only reuse an existing order when we have an explicit table
    // AND the passed order is reusable on that table. Without a table check
    // an old order object (from a previous session) could be reused,
    // duplicating orderCodes for completely new orders.
    if (isReusableTableOrder(order) && (order?._id || order?.generatedId) && currentTable?._id && normalizeId(order?.tableId) === normalizeId(currentTable?._id)) {
        return {
            resolvedOrderId: order?._id || order?.generatedId,
            resolvedOrder: order,
            isExistingOrder: true,
        };
    }

    if (!currentTable?._id && !explicitOrderId) {
        return {
            resolvedOrderId: generateObjectId(),
            resolvedOrder: order || null,
            isExistingOrder: false,
        };
    }

    // ── Candidate pool: server-confirmed orders + queued OPEN orders ─────────
    const candidates = [];

    try {
        const storedOrders = await AsyncStorage.getItem('orders');
        if (storedOrders) {
            const parsedOrders = JSON.parse(storedOrders);
            if (Array.isArray(parsedOrders)) {
                candidates.push(...parsedOrders);
            }
        }
    } catch (error) {
        console.warn('[paymentHelper] Failed to read cached orders while resolving table order:', error?.message || error);
    }

    // IMPORTANT: Do not use `lastOrderResponse` for table-order resolution.
    // It only reflects the latest created order globally and can belong to
    // another table/session, which may cause accidental orderCode reuse.

    // Surface locally-queued OPEN orders for THIS table. These have not finished
    // syncing yet, so without them, adding items to an order that is still in the
    // sync queue would be treated as a brand-new order and create a duplicate on
    // the table. We reuse their stable generatedId / orderCode (never minting
    // new identifiers), so the backend merges the items into the same order.
    if (currentTable?._id) {
        candidates.push(...(await getQueuedOpenOrdersForTable(currentTable._id)));
    }

    const uniqueCandidates = [];
    const seenCandidateKeys = new Set();

    candidates.forEach((candidate) => {
        const candidateKey = candidate?._id || candidate?.generatedId || `${candidate?.tableId || ''}:${candidate?.orderCode || ''}`;
        if (!candidateKey || seenCandidateKeys.has(candidateKey)) {
            return;
        }

        seenCandidateKeys.add(candidateKey);
        uniqueCandidates.push(candidate);
    });

    // Only match by explicit orderId when we also have a currentTable context.
    // This prevents stale orderId references from a previous session from
    // accidentally reusing an old order (and its orderCode).
    if (explicitOrderId && currentTable?._id) {
        const normalizedExplicitOrderId = normalizeId(explicitOrderId);
        const matchingOrderById = uniqueCandidates.find((candidate) => {
            const candidateId = candidate?._id || candidate?.generatedId;
            return normalizeId(candidateId) === normalizedExplicitOrderId;
        });

        if (matchingOrderById && isReusableTableOrder(matchingOrderById)) {
            return {
                resolvedOrderId: matchingOrderById?._id || matchingOrderById?.generatedId,
                resolvedOrder: matchingOrderById,
                isExistingOrder: true,
            };
        }
    }

    if (currentTable?._id) {
        const normalizedCurrentTableId = normalizeId(currentTable._id);
        const matchingActiveTableOrder = uniqueCandidates.find((candidate) => {
            return isReusableTableOrder(candidate) && normalizeId(candidate?.tableId) === normalizedCurrentTableId;
        });

        if (matchingActiveTableOrder) {
            return {
                resolvedOrderId: matchingActiveTableOrder?._id || matchingActiveTableOrder?.generatedId,
                resolvedOrder: matchingActiveTableOrder,
                isExistingOrder: true,
            };
        }
    }

    // No matching active order found – this is a NEW order.
    // Always generate a new ID; never mark as existing just because
    // an orderId was supplied (it may be stale from a prior session).
    return {
        resolvedOrderId: generateObjectId(),
        resolvedOrder: order || null,
        isExistingOrder: false,
    };
};

const scheduleBackgroundTask = (task, onError) => {
    const runTask = () => {
        Promise.resolve()
            .then(task)
            .catch((error) => {
                console.error('[paymentHelper] Background payment side effect failed:', error);
                if (onError) {
                    onError(error);
                }
            });
    };

    if (InteractionManager?.runAfterInteractions) {
        InteractionManager.runAfterInteractions(runTask);
        return;
    }

    globalThis.setTimeout(runTask, 0);
};

/**
 * Report an error to the server and handle it appropriately
 * @param {Error} error - The error object
 * @param {Object} context - Additional context information
 */
const reportError = async (error, context = {}) => {
    try {
        // Report the error to the server
        await ErrorReportingService.reportError(error, context);
    } catch (reportingError) {
        // Don't throw from the error reporter to avoid infinite loops
        console.error('Failed to report error:', reportingError);
    }
};


const buildFoodLookup = (foods = []) => {
    if (!Array.isArray(foods)) {
        return {};
    }

    return foods.reduce((lookup, food) => {
        if (food?._id) {
            lookup[food._id] = food;
        }

        return lookup;
    }, {});
};

const resolveNormalizedLineItemObject = (lineItem = {}, foodLookup = {}) => {
    if (lineItem?.item && typeof lineItem.item === 'object') {
        return lineItem.item;
    }

    const candidateIds = [
        lineItem?.itemId,
        lineItem?.item?._id,
        lineItem?.item,
        lineItem?._id,
        lineItem?.id,
    ].filter(Boolean);

    const hydratedItem = candidateIds
        .map((candidateId) => foodLookup[candidateId])
        .find(Boolean);

    return hydratedItem || lineItem;
};

const normalizeMockOrderExtras = (extras = [], foodLookup = {}) => {
    if (!Array.isArray(extras)) {
        return [];
    }

    return extras.map((extra) => {
        const normalizedExtraItem = resolveNormalizedLineItemObject(extra, foodLookup);

        return {
            ...extra,
            item: normalizedExtraItem,
        };
    });
};

const normalizeMockOrderItems = (items = [], foodLookup = {}) => {
    if (!Array.isArray(items)) {
        return [];
    }

    return items.map((item) => {
        const normalizedItemObject = resolveNormalizedLineItemObject(item, foodLookup);

        return {
            ...item,
            item: normalizedItemObject,
            itemId: item?.itemId || normalizedItemObject?._id || item?._id,
            extras: normalizeMockOrderExtras(item?.extras, foodLookup),
        };
    });
};

export const send_storeOrder = async (cartTotal, cartItems, paymentType, paidAmount, discountsForWholeCart, vatInvoiceData, needInvoice, currentTable, serviceFeeAmount, isSavedForLater = false, alreadySentCartItems, orderId, paymentTypes, tip, originalAmount, selectedVipCard, userData, selectedItemsToPay, order, proform, deliveryFee) => {
    const isDineInOrder = Boolean(isSavedForLater || selectedItemsToPay?.length > 0 || currentTable);
    const resolvedOrderType = isDineInOrder ? "dineIn" : normalizeOrderType(userData?.orderType, "takeAway");

    console.log("Send order to save (instant-success): ", {
        orderType: resolvedOrderType,
        cartTotal,
        paymentType,
        orderId,
    });

    try {
        const allLineItems = [...(cartItems || []), ...(alreadySentCartItems || [])];
        // Independent reads run in parallel; the big `foods` catalog is served
        // from an in-memory lookup instead of being re-read and re-parsed per send.
        const [restaurantPlaceData, foodLookup, { resolvedOrderId, resolvedOrder, isExistingOrder }] = await Promise.all([
            AsyncStorage.getItem('restaurantPlace'),
            lineItemsNeedFoodLookup(allLineItems)
                ? getCachedFoodLookup(() => AsyncStorage.getItem('foods'), buildFoodLookup)
                : Promise.resolve({}),
            resolveExistingTableOrder({ currentTable, orderId, order }),
        ]);
        const restaurantPlace = restaurantPlaceData ? JSON.parse(restaurantPlaceData) : null;
        const resolvedOrderCode = isExistingOrder
            ? (resolvedOrder?.orderCode || order?.orderCode || generateHappyId())
            : generateHappyId();

        console.log("[send_storeOrder] Order resolution:", {
            isExistingOrder,
            resolvedOrderId,
            resolvedOrderCode,
            hasCurrentTable: !!currentTable?._id,
            passedOrderId: orderId,
            passedOrderCode: order?.orderCode,
        });

        // A payment covering the whole open tab must close it on the server even
        // when the backend's own re-pricing differs from what was charged here.
        const settlesTableOrder = isFullTableSettlement({
            currentTable,
            isExistingOrder,
            existingOrder: resolvedOrder || order,
            paidLineItems: allLineItems,
            isSavedForLater,
            proforma: proform,
            selectedItemsToPay,
        });

        // ────────────────────────────────────────────────────────────────────
        // STEP 1 — Prepare the order payload (identical online & offline)
        // ────────────────────────────────────────────────────────────────────
        const orderData = {
            cartTotal,
            cartItems,
            paymentType,
            paidAmount,
            discountsForWholeCart,
            vatInvoiceData,
            needInvoice,
            currentTable,
            serviceFeeAmount,
            isSavedForLater,
            alreadySentCartItems,
            orderId: resolvedOrderId,
            paymentTypes,
            tip,
            originalAmount,
            selectedVipCard,
            userData,
            selectedItemsToPay,
            order: resolvedOrder || order,
            proform,
            deliveryFee,
            _stableGeneratedId: resolvedOrderId,
            _stableOrderCode: resolvedOrderCode,
            _settlesTableOrder: settlesTableOrder,
        };

        const normalizedPaymentTypes = Array.isArray(paymentTypes)
            ? paymentTypes.map((payment) => ({
                ...payment,
                paymentType: payment?.paymentType || payment?.type || payment?.method || '',
                amount: Number(payment?.amount || 0),
            }))
            : [];

        // ────────────────────────────────────────────────────────────────────
        // STEP 2 — Persist to SQLite PendingOrders table (<100 ms)
        //          This is the single source of truth for unsent orders.
        // ────────────────────────────────────────────────────────────────────
        await insertPendingOrder(orderData, resolvedOrderId);

        // ────────────────────────────────────────────────────────────────────
        // STEP 3 — Build a local mock response so the UI / receipt printer
        //          can proceed immediately without waiting for the API.
        // ────────────────────────────────────────────────────────────────────
        const mockResponseItems = normalizeMockOrderItems(allLineItems, foodLookup);

        const mockResponse = {
            _id: resolvedOrderId,
            generatedId: resolvedOrderId,
            orderCode: resolvedOrderCode || generateHappyId(),
            items: mockResponseItems,
            orderSum: cartTotal,
            discounts: discountsForWholeCart,
            paymentType: paymentType || normalizedPaymentTypes?.[0]?.paymentType || 'temporary',
            paymentTypes: normalizedPaymentTypes,
            restaurantPlace: restaurantPlace?._id || currentTable?.restaurantPlace || '',
            isOfflineOrder: true,
            // Distinct status so the floor-plan colours the table immediately as
            // an unsynced/open order (TableMapScreen `_getColor` → pending_sync).
            // Without a status the table would still render as "free" even though
            // the order exists in state, leaving the duplicate-order window open.
            status: isExistingOrder ? (resolvedOrder?.status || order?.status || 'pending_sync') : 'pending_sync',
            // `serviceFee` = %, `serviceFeePrice` = HUF (same contract as the synced
            // order). Passing the HUF amount as `serviceFee` made the VAT breakdown
            // and the Fiscat payload drop the szervízdíj (>100 read as an invalid %).
            ...buildOrderServiceFeeFields(isDineInOrder ? serviceFeeAmount : 0, restaurantPlace?.serviceFee),
            deliveryFee: Math.max(0, Number(deliveryFee) || 0),
            orderType: resolvedOrderType,
            tableId: currentTable?._id,
            tableName: currentTable?.name,
            VIPCard: selectedVipCard || null,
            proforma: proform || false,
            settlesTableOrder,
        };

        // `cartTotal` arrives with OR without the szervízdíj depending on the
        // payment path (PaymentModal adds it, the table payment paths don't), so
        // it can't be the local order's `orderSum`. Use the backend contract
        // instead — products − discount + service fee + delivery fee — computed
        // from the same charged rows the Fiscat register receives.
        const mockTotals = computeReceiptTotals(mockResponse);
        if (mockTotals) {
            mockResponse.productsPrice = mockTotals.productsAmount;
            mockResponse.orderSum = mockTotals.total;
        }

        // Also store in AsyncStorage for legacy code paths that read from there
        await AsyncStorage.multiSet([
            ['lastOrderResponse', JSON.stringify(mockResponse)],
            ['orderStored', 'true'],
        ]);

        // ────────────────────────────────────────────────────────────────────
        // STEP 4 — Kick the SyncManager (non-blocking).
        //          It will POST the order when connectivity allows.
        // ────────────────────────────────────────────────────────────────────
        kickSyncQueue().catch(err => {
            console.warn("[send_storeOrder] SyncManager kick failed (will retry):", err.message);
        });


        return mockResponse;
    } catch (error) {
        console.error("Error in send_storeOrder:", error);


        // Report if possible
        if (await isOnline()) {
            await reportError(error, {
                function: 'send_storeOrder',
                orderData: { cartTotal, orderId, paymentType, isSavedForLater, tableId: currentTable?._id }
            });
        }
        throw error;
    }
}

export const handleVivaPayment = (paidAmount) => {
    const paymentAmount = Number(paidAmount) * 100; // Amount in minor units (e.g., cents)
    return VivaPaymentModule.initiatePayment(paymentAmount);
};

let lastNovoCharge = null;

/** Human-readable description of the last approved NPIPE charge (for "charged but order not saved" alerts). */
const novoChargeNote = () => {
    const r = lastNovoCharge || {};
    return ['NPIPE', r.cardBrand, r.maskedCard, r.authCode && `auth ${r.authCode}`, r.slipNumber && `slip ${r.slipNumber}`, r.referenceNumber && `ref ${r.referenceNumber}`]
        .filter(Boolean)
        .join(' ');
};

/**
 * Charge the card on the Novo (NPIPE) terminal. Calls sendToCashier only on approval.
 * @param {number|string} paidAmount amount in major units
 * @param {Function} sendToCashier called after an approved payment
 * @param {Function} printReceiptAndTicket unused here (receipts are printed by the NPIPE flow)
 * @param {string} [orderCode] used for the invoice reference
 * @param {string} [pttpType] acquirer override (e.g. 'Sca' for Szép kártya)
 * @returns {Promise<boolean>} true when approved, false on decline / cancel / timeout / error (outcome dialog already shown)
 */
export const handleNovoSzepPayment = async (paidAmount, sendToCashier, printReceiptAndTicket, orderCode = '', pttpType) => {
    const { payWithNpipe } = require('../paymentModules/npipe/npipePayments');
    const { createNpipeStatusHandler, showNpipeOutcome } = require('../components/NpipePaymentDialog');
    const res = await payWithNpipe({
        moduleKey: 'pos',
        amount: Number(paidAmount),
        reference: orderCode || String(Date.now()),
        pttpType,
        onStatus: createNpipeStatusHandler(Number(paidAmount)),
    });
    showNpipeOutcome(res, Number(paidAmount));
    if (res.approved) {
        lastNovoCharge = res.result || null;
        if (res.result && res.result.amountMatches === false) {
            // Terminal approved a different amount than requested (e.g. DCC / partial): the cashier must see it.
            Alert.alert(i18n.t('npipe_card_payment', { defaultValue: 'Card payment' }), `${novoChargeNote()} – amount ${res.result.amount}`);
        }
        await sendToCashier();
        return true;
    }
    return false;
};

export const handleTeyaPayment = async (paidAmount, sendToCashier, printReceiptAndTicket, orderCode = '') => {
    console.log("handleTeyaPayment called with paidAmount:", paidAmount);

    // Import the TeyaPaymentsModule
    const TeyaPaymentsModule = require('../paymentModules/TeyaPaymentsModule').default;

    try {
        // Use the TeyaPaymentsModule to initiate payment
        await TeyaPaymentsModule.initiatePayment(
            paidAmount,
            0, // No tip amount
            orderCode,
            // onComplete callback
            async () => {
                console.log("Teya payment completed");
                // When payment is completed, call sendToCashier and printReceiptAndTicket
                await sendToCashier();
                await printReceiptAndTicket();
            },
            // onCancel callback
            () => {
                console.log("Teya payment cancelled");
                throw new Error('Payment cancelled');
            }
        );

        return true;
    } catch (error) {
        console.error("Error in handleTeyaPayment:", error);
        throw error;
    }
};


export const handlePayment = async (paymentType, paidAmount, originalAmount, setProcessing, navigation, cartTotal, setCart, cartItems, sendData, onClose, originalCartTotal, productsPrice, discountsForWholeCart, vatInvoiceData, needInvoice, successPayment = false, currentTable, serviceFeeAmount, alreadySentCartItems, orderId, paymentTypes, tip, selectedVipCard, selectedItemsToPay, order, sendToCashRegister = true) => {
    if (!paymentType && (!paymentTypes || paymentTypes && paymentTypes.length === 0)) {
        if (Platform.OS === "android") {
            return ToastAndroid.showWithGravity(i18n.t('payment_select_method'), 5000, ToastAndroid.CENTER);
        } else {
            setProcessing(false);
            return alert(i18n.t('payment_select_method'));
        }
    }
    if (paymentTypes && paymentTypes.length > 0) {
        const paymentsSum = paymentTypes.reduce((acc, curr) => { return acc + curr.amount }, 0)
        if (Math.round(paymentsSum) + 4 < Math.round(originalAmount) || (paymentType !== "Készpénz" && paymentsSum + 4 < Math.round(originalAmount))) {
            if (Platform.OS === "android") {
                return ToastAndroid.showWithGravity(i18n.t('payment_amount_too_low'), 5000, ToastAndroid.CENTER);
            } else {
                setProcessing(false);
                return alert(i18n.t('payment_amount_too_low'));
            }
        }
    } else if (paidAmount + 4 < originalAmount || (paymentType !== "Készpénz" && paidAmount + 4 < Math.round(originalAmount))) {
        if (Platform.OS === "android") {
            return ToastAndroid.showWithGravity(i18n.t('payment_amount_cannot_be_less_than_original'), 5000, ToastAndroid.CENTER);
        } else {
            setProcessing(false);
            return alert(i18n.t('payment_amount_cannot_be_less_than_original'));
        }
    }
    if (paidAmount === "") {
        if (Platform.OS === "android") {
            return ToastAndroid.showWithGravity(i18n.t('payment_enter_amount'), 5000, ToastAndroid.CENTER);
        } else {
            setProcessing(false);
            return alert(i18n.t('payment_enter_amount'));
        }
    }
    setProcessing(true);

    const resolvePrintResultError = (result, fallbackMessage) => {
        if (result?.skipped === true) {
            return null;
        }

        if (result === true) {
            return null;
        }

        if (result?.error instanceof Error) {
            return result.error;
        }

        if (result && result.error) {
            return new Error(result.error?.message || fallbackMessage);
        }

        if (result === false) {
            return new Error(fallbackMessage);
        }

        return null;
    };

    // The payment screen's "Cash register / fiscal printing" switch decides this;
    // when it is off the receipt must never reach the register (card terminal
    // success callbacks included).
    const sendToCashier = (storedOrder) => {
        if (!sendToCashRegister) {
            return Promise.resolve(true);
        }

        return FiscatQRService.sendToCashier(storedOrder, sendData);
    };

    const navigateAfterPayment = (orderResponse) => {
        setCart([]);
        setProcessing(false);

        // Read at call time (not import time) so a fold/unfold since startup is honoured.
        if (Dimensions.get('window').width >= POS_TABLET_BREAKPOINT) {
            onClose();
            navigation.navigate("HomeScreen", { paymentSuccess: true, orderResponse });
        } else {
            navigation.navigate("SuccessPayment", { orderResponse });
        }
    };

    const printReceiptAndTicket = (printedOrder) => {
        if (Platform.OS !== "android") {
            return Promise.resolve(true);
        }

        // Shared across the retry dialog's attempts: a retry reprints only the
        // printers that failed, never the receipt/tickets that already came out.
        const printedTargets = new Set();

        const attemptPrinting = async () => {
            const hasConfiguredPrinter = await hasAnyConfiguredPrinter();
            if (!hasConfiguredPrinter) {
                return true;
            }

            const receiptResult = await RawBTPrintTicket(printedOrder, { showErrorDialog: false, printedTargets });
            const receiptError = resolvePrintResultError(receiptResult, i18n.t('printing_failed'));

            if (receiptError) {
                throw receiptError;
            }

            if (cartItems.length > 0) {
                const kitchenResult = await RawBTPrintKitchen(printedOrder, printedOrder?.items || [], { showErrorDialog: false, printedTargets });
                const kitchenError = resolvePrintResultError(kitchenResult, i18n.t('printing_failed'));

                if (kitchenError) {
                    throw kitchenError;
                }
            }

            return true;
        };

        const showRetryDialog = () => {
            return new Promise((resolve) => {
                if (Platform.OS === "android") {
                    Alert.alert(
                        i18n.t('error'),
                        i18n.t('printing_failed'),
                        [
                            {
                                text: i18n.t('cancel'),
                                onPress: () => {
                                    console.log('Printing cancelled');
                                    resolve(false);
                                },
                                style: 'cancel',
                            },
                            {
                                text: i18n.t('retry'),
                                onPress: async () => {
                                    console.log('Retrying printing');
                                    try {
                                        await attemptPrinting();
                                        resolve(true);
                                    } catch (retryError) {
                                        console.error('Retry printing failed:', retryError);
                                        showRetryDialog().then((result) => resolve(result));
                                    }
                                },
                            },
                        ],
                        { cancelable: false }
                    );
                } else {
                    alert(i18n.t('printing_failed'));
                    resolve(false);
                }
            });
        };

        return new Promise((resolve) => {
            attemptPrinting()
                .then(() => resolve(true))
                .catch((printError) => {
                    console.error('Initial printing failed:', printError);
                    showRetryDialog().then((result) => resolve(result));
                });
        });
    };

    const finalizeSuccessfulPayment = async ({ context, orderResponse }) => {
        const reportAsyncTaskError = (error, taskContext) => {
            reportError(error, {
                function: 'handlePayment',
                context: taskContext,
                paymentType,
                orderId,
                orderCode: orderResponse?.orderCode,
            });
        };

        scheduleBackgroundTask(
            () => sendToCashier(orderResponse),
            (error) => reportAsyncTaskError(error, `${context}:sendToCashier`)
        );

        // Free the paid table on the floor plan now: the payment still has to
        // sync through the offline queue, and until the server echoes `closed`
        // the local tab would keep the table busy.
        if (orderResponse?.settlesTableOrder) {
            try {
                Observables.call('orders:closeOptimistic', {
                    orderId: orderResponse._id,
                    tableId: orderResponse.tableId || currentTable?._id,
                });
            } catch (_) { /* OrdersContext not mounted yet; the next sync frees it */ }
        }

        // Show the success screen right away. Printing (receipt, then kitchen
        // ticket) continues in the background; a failure still surfaces the
        // retry dialog and is reported, but the cashier no longer waits
        // seconds on the printer before the POS becomes usable again.
        navigateAfterPayment(orderResponse);

        scheduleBackgroundTask(
            async () => {
                const printResult = await printReceiptAndTicket(orderResponse);

                if (printResult !== true) {
                    reportAsyncTaskError(new Error('Printing cancelled or failed after retries'), `${context}:printReceiptAndTicket`);
                }
            },
            (error) => reportAsyncTaskError(error, `${context}:printReceiptAndTicket`)
        );

        return true;
    };

    // Check if Teya terminal is selected for Bankkártya payment
    // If so, we must initiate Teya payment FIRST and only store the order after success
    if (paymentType === "Bankkártya" && !successPayment) {
        const paymentTerminal = await AsyncStorage.getItem('selectedCardTerminal');
        if (paymentTerminal === "Teya") {
            console.log("Teya terminal selected – initiating Teya payment BEFORE storing order");
            return new Promise(async (resolve, reject) => {
                try {
                    await handleTeyaPayment(
                        paidAmount,
                        // sendToCashier callback – called only on Teya SUCCESS
                        async () => {
                            console.log("Teya payment succeeded – now storing order");
                            try {
                                const storeOrderResponse = await send_storeOrder(cartTotal, cartItems, paymentType, paidAmount, discountsForWholeCart, vatInvoiceData, needInvoice, currentTable, serviceFeeAmount, false, alreadySentCartItems, orderId, paymentTypes, tip, originalAmount, selectedVipCard, undefined, selectedItemsToPay, order);
                                if (!storeOrderResponse) {
                                    setProcessing(false);
                                    const msg = i18n.t('payment_invoice_save_failed');
                                    if (Platform.OS === "android") {
                                        ToastAndroid.showWithGravity(msg, ToastAndroid.LONG, ToastAndroid.CENTER);
                                    } else {
                                        alert(msg);
                                    }
                                    resolve();
                                    return;
                                }
                                await finalizeSuccessfulPayment({
                                    context: 'Teya payment',
                                    orderResponse: storeOrderResponse,
                                });
                                resolve();
                            } catch (storeError) {
                                console.error("Error storing order after Teya payment:", storeError);
                                await reportError(storeError, {
                                    function: 'handlePayment',
                                    context: 'Teya post-payment order store',
                                    paymentType,
                                    orderId
                                });
                                setProcessing(false);
                                resolve();
                            }
                        },
                        // printReceiptAndTicket callback
                        () => { },
                        orderId
                    );
                } catch (teyaError) {
                    console.error("Teya payment failed or cancelled:", teyaError);
                    setProcessing(false);
                    resolve();
                }
            });
        }
    }

    // Check if Novo terminal is selected (Bankkártya / Szép kártya)
    // If so, we must initiate Novo payment FIRST and only store the order after success
    if ((paymentType === "Bankkártya" || paymentType === "Szép kártya") && !successPayment) {
        const paymentTerminal = await AsyncStorage.getItem('selectedCardTerminal');
        if (paymentTerminal === "Novo Terminal") {
            console.log("Novo terminal selected – initiating Novo payment BEFORE storing order");
            return new Promise(async (resolve, reject) => {
                try {
                    const novoApproved = await handleNovoSzepPayment(
                        paidAmount,
                        // sendToCashier callback – called only on Novo SUCCESS
                        async () => {
                            console.log("Novo payment succeeded – now storing order");
                            try {
                                const storeOrderResponse = await send_storeOrder(cartTotal, cartItems, paymentType, paidAmount, discountsForWholeCart, vatInvoiceData, needInvoice, currentTable, serviceFeeAmount, false, alreadySentCartItems, orderId, paymentTypes, tip, originalAmount, selectedVipCard, undefined, selectedItemsToPay, order);
                                if (!storeOrderResponse) {
                                    setProcessing(false);
                                    // The card is already charged: tell the cashier exactly what was approved.
                                    Alert.alert(i18n.t('npipe_card_payment', { defaultValue: 'Card payment' }), `${i18n.t('payment_invoice_save_failed')}\n${novoChargeNote()}`);
                                    resolve(false);
                                    return;
                                }
                                await finalizeSuccessfulPayment({
                                    context: 'Novo payment',
                                    orderResponse: storeOrderResponse,
                                });
                                resolve();
                            } catch (storeError) {
                                console.error("Error storing order after Novo payment:", storeError);
                                await reportError(storeError, {
                                    function: 'handlePayment',
                                    context: 'Novo post-payment order store',
                                    paymentType,
                                    orderId
                                });
                                setProcessing(false);
                                Alert.alert(i18n.t('npipe_card_payment', { defaultValue: 'Card payment' }), `${i18n.t('payment_invoice_save_failed')}\n${novoChargeNote()}`);
                                resolve(false);
                            }
                        },
                        // printReceiptAndTicket callback
                        () => { },
                        orderId,
                        paymentType === "Szép kártya" ? 'Sca' : undefined
                    );
                    if (novoApproved === false) {
                        // Declined / cancelled / unknown outcome: nothing was stored, callers see `false`.
                        setProcessing(false);
                        resolve(false);
                    }
                } catch (novoError) {
                    console.error("Novo payment failed or cancelled:", novoError);
                    setProcessing(false);
                    resolve(false);
                }
            });
        }
    }

    const storeOrderResponse = await send_storeOrder(cartTotal, cartItems, paymentType, paidAmount, discountsForWholeCart, vatInvoiceData, needInvoice, currentTable, serviceFeeAmount, false, alreadySentCartItems, orderId, paymentTypes, tip, originalAmount, selectedVipCard, undefined, selectedItemsToPay, order);
    //console.log("storeOrderResponse: ", storeOrderResponse);
    try {
        if (!storeOrderResponse) {
            if (Platform.OS === "android") {
                setProcessing(false);
                return ToastAndroid.showWithGravity(i18n.t('payment_invoice_save_failed'), ToastAndroid.LONG, ToastAndroid.CENTER, 50);
            } else {
                console.log("[send_storeOrder] Invoice save failed")
                setProcessing(false);
                return alert(i18n.t('payment_invoice_save_failed'));
            }
        }
    } catch (e) {
        setProcessing(false);
        // Report the error to the server
        await reportError(e, {
            function: 'handlePayment',
            context: 'storeOrderResponse validation',
            paymentType,
            orderId
        });

        if (Platform.OS === "android") {
            return ToastAndroid.showWithGravity(i18n.t('payment_invoice_save_failed'), ToastAndroid.LONG, ToastAndroid.CENTER);
        } else {
            console.log("[handlePayment] Invoice save validation failed")
            return alert(i18n.t('payment_invoice_save_failed'));
        }
    }

    try {
        console.log("[handlePayment] Payment type:", paymentType);
        if (paymentType === "Bankkártya") {
            return new Promise(async (resolve) => {
                const paymentTerminal = await AsyncStorage.getItem('selectedCardTerminal');
                console.log("Payment type is Bankkártya, paymentTerminal:", paymentTerminal, "successPayment:", successPayment);

                if (paymentTerminal && !successPayment && paymentTerminal === "Viva Payments") {
                    console.log("Using Viva Payments terminal");
                    return await handleVivaPayment(paidAmount, sendToCashier, printReceiptAndTicket);
                }
                if (paymentTerminal && !successPayment && paymentTerminal === "Novo Terminal") {
                    console.log("Using Novo Terminal");
                    return await handleNovoSzepPayment(paidAmount, sendToCashier, printReceiptAndTicket);
                }
                if (!paymentTerminal || successPayment) {
                    console.log("No payment terminal selected or successPayment is true");
                    await finalizeSuccessfulPayment({ context: 'Bankkártya payment', orderResponse: storeOrderResponse });
                    resolve();
                }
            });
        }
        if (paymentType === "Szép kártya") {
            return new Promise(async (resolve) => {
                const paymentTerminal = await AsyncStorage.getItem('selectedCardTerminal');

                if (paymentTerminal && !successPayment && paymentTerminal === "Novo Terminal") return await handleNovoSzepPayment(paidAmount, sendToCashier, printReceiptAndTicket); // Implement this function as needed
                if (!paymentTerminal || successPayment) {
                    await finalizeSuccessfulPayment({ context: 'Szép kártya payment', orderResponse: storeOrderResponse });
                    resolve();
                }
            });
        }

        if (paymentType === "Készpénz") {
            await finalizeSuccessfulPayment({ context: 'Készpénz payment', orderResponse: storeOrderResponse });
            return;
        }
        if (paymentType === "Utalvány") {
            return new Promise(async (resolve) => {
                await finalizeSuccessfulPayment({ context: 'Utalvány payment', orderResponse: storeOrderResponse });
                resolve();
            });
        }
        if (!paymentType && paymentTypes && paymentTypes.length > 0) {
            return new Promise(async (resolve) => {
                await finalizeSuccessfulPayment({ context: 'Multi payment', orderResponse: storeOrderResponse });
                resolve();
            });
        }
    } catch (error) {
        setProcessing(false);
        console.log(error);

        await reportError(error, {
            function: 'handlePayment',
            context: 'payment processing',
            paymentType,
            orderId
        });

        if (Platform.OS === "android") {
            return ToastAndroid.showWithGravity(i18n.t('payment_failed'), ToastAndroid.LONG, ToastAndroid.CENTER);
        } else {
            return alert(i18n.t('payment_failed'));
        }
    }
};

